import styled from "@emotion/styled";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { FONT_WEIGHT_MEDIUM } from "../utilities/constants";
import { zIndexes } from "../utilities/zIndexes";

/*
  A specific style of draggable component used in side panels.
  Currently used in the response editor and composable canvas.
*/
type Props = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
};

export const PanelDragItem = forwardRef<HTMLDivElement, Props>(({ children, ...domAttrs }, ref) => (
  <DraggableContainer ref={ref} {...domAttrs}>
    {children}
  </DraggableContainer>
));
PanelDragItem.displayName = "PanelDragItem";

const DraggableContainer = styled.div`
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 6px;

  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  background: ${({ theme }) => theme.surface};
  font-size: ${({ theme }) => theme.fontSize};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  line-height: 1.4em;

  position: relative;
  z-index: ${zIndexes.triggersOverlaySidebar};
  overflow: hidden;
  transition: 0.2s ease-in-out;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.surfaceVariant};
    border-color: transparent;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    transition: background 0.2s ease-in-out;
  }

  .fa-icon-wrapper {
    font-size: 18px;
  }

  &.blue {
    .fa-icon-wrapper {
      color: ${({ theme }) => theme.primary};
    }
    &.active {
      border-color: ${({ theme }) => theme.primary};
    }
    &:hover:before {
      background: ${({ theme }) => theme.primary};
    }
  }

  &.orange {
    .fa-icon-wrapper {
      color: ${({ theme }) => theme.warning};
    }
    &.active {
      border-color: ${({ theme }) => theme.warning};
    }
    &:hover:before {
      background: ${({ theme }) => theme.warning};
    }
  }

  &.purple {
    .fa-icon-wrapper {
      color: ${({ theme }) => theme.secondary};
    }
    &.active {
      border-color: ${({ theme }) => theme.secondary};
    }
    &:hover:before {
      background: ${({ theme }) => theme.secondary};
    }
  }

  &.disabled {
    color: ${({ theme }) => theme.generalTextIconVariant};
    span,
    .fa-icon-wrapper {
      color: ${({ theme }) => theme.generalTextIconVariant};
    }
  }
`;
